import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AdminShell from './AdminShell';
import ImageField from './ImageField';
import { useContentStore } from '../../context/ContentContext';
import { CONTENT_SCHEMA, cloneValue } from '../../content/siteContent';

const inputClass = 'w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary-red';

/** Every image path currently in use, so the picker can offer them too. */
const collectImages = (content) => {
    const found = [];
    const walk = (value) => {
        if (typeof value === 'string') {
            if (value.startsWith('/images/')) found.push(value);
        } else if (Array.isArray(value)) {
            value.forEach(walk);
        } else if (value && typeof value === 'object') {
            Object.values(value).forEach(walk);
        }
    };
    walk(content);
    return Array.from(new Set(found));
};

/** One editable value - used both for top level fields and for list item fields. */
const FieldEditor = ({ field, value, onChange, extraImages }) => {
    switch (field.type) {
        case 'image':
            return (
                <ImageField
                    label={field.label}
                    value={value || ''}
                    onChange={onChange}
                    extraImages={extraImages}
                />
            );

        case 'textarea':
            return (
                <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">{field.label}</label>
                    <textarea
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        rows={3}
                        className={`${inputClass} resize-y`}
                    />
                </div>
            );

        case 'lines':
            return (
                <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">{field.label}</label>
                    <textarea
                        value={Array.isArray(value) ? value.join('\n') : (value || '')}
                        onChange={(e) => onChange(e.target.value.split('\n'))}
                        onBlur={(e) => onChange(
                            e.target.value.split('\n').map(line => line.trim()).filter(Boolean)
                        )}
                        rows={Math.max(3, (Array.isArray(value) ? value.length : 1) + 1)}
                        className={`${inputClass} resize-y`}
                    />
                    <p className="text-gray-500 text-xs mt-1">One entry per line. Blank lines are removed.</p>
                </div>
            );

        case 'url':
        case 'text':
        default:
            return (
                <div>
                    <label className="block text-gray-300 text-sm font-medium mb-2">{field.label}</label>
                    <input
                        type="text"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        className={inputClass}
                    />
                </div>
            );
    }
};

/** Repeatable group of fields (cards, steps, machines, people...). */
const ListEditor = ({ field, value, onChange, extraImages }) => {
    const items = Array.isArray(value) ? value : [];
    const itemLabel = field.itemLabel || 'Item';

    const updateItem = (index, key, itemValue) => {
        const next = items.map((item, i) => (i === index ? { ...item, [key]: itemValue } : item));
        onChange(next);
    };

    const moveItem = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= items.length) return;
        const next = [...items];
        const [removed] = next.splice(index, 1);
        next.splice(newIndex, 0, removed);
        onChange(next);
    };

    const removeItem = (index) => onChange(items.filter((_, i) => i !== index));

    const addItem = () => {
        const blank = {};
        field.itemFields.forEach(f => {
            blank[f.key] = f.type === 'lines' ? [] : '';
        });
        onChange([...items, blank]);
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <label className="block text-gray-300 text-sm font-medium">
                    {field.label} <span className="text-gray-500">({items.length})</span>
                </label>
                <button
                    type="button"
                    onClick={addItem}
                    className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded text-xs font-medium transition-colors"
                >
                    + Add {itemLabel.toLowerCase()}
                </button>
            </div>

            <div className="space-y-4">
                {items.map((item, index) => (
                    <div key={index} className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-gray-400 text-xs font-medium uppercase tracking-wide">
                                {itemLabel} {index + 1}
                            </span>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => moveItem(index, 'up')}
                                    disabled={index === 0}
                                    className={`p-1.5 rounded ${index === 0 ? 'text-gray-600' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}
                                    title="Move up"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => moveItem(index, 'down')}
                                    disabled={index === items.length - 1}
                                    className={`p-1.5 rounded ${index === items.length - 1 ? 'text-gray-600' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}
                                    title="Move down"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => removeItem(index)}
                                    className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded"
                                    title="Remove"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {field.itemFields.map(itemField => (
                                <FieldEditor
                                    key={itemField.key}
                                    field={itemField}
                                    value={item[itemField.key]}
                                    onChange={(v) => updateItem(index, itemField.key, v)}
                                    extraImages={extraImages}
                                />
                            ))}
                        </div>
                    </div>
                ))}

                {items.length === 0 && (
                    <p className="text-gray-500 text-sm italic">
                        Nothing here yet - use "Add {itemLabel.toLowerCase()}" above.
                    </p>
                )}
            </div>
        </div>
    );
};

const AdminContent = () => {
    const { content, isLoading, error, storageMode, saveContent, resetContent } = useContentStore();

    const [draft, setDraft] = useState(() => cloneValue(content));
    const [activePage, setActivePage] = useState(CONTENT_SCHEMA[0].key);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [status, setStatus] = useState('');
    const [saveError, setSaveError] = useState('');
    const [showResetConfirm, setShowResetConfirm] = useState(false);

    // Pick up content once it has loaded, but never clobber unsaved edits
    useEffect(() => {
        if (!isDirty) {
            setDraft(cloneValue(content));
        }
    }, [content, isDirty]);

    // Warn before leaving with unsaved edits
    useEffect(() => {
        const handler = (e) => {
            if (!isDirty) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    const extraImages = useMemo(() => collectImages(draft), [draft]);

    const page = CONTENT_SCHEMA.find(p => p.key === activePage);

    const setFieldValue = useCallback((pageKey, groupKey, fieldKey, value) => {
        setDraft(prev => ({
            ...prev,
            [pageKey]: {
                ...prev[pageKey],
                [groupKey]: {
                    ...(prev[pageKey] ? prev[pageKey][groupKey] : {}),
                    [fieldKey]: value
                }
            }
        }));
        setIsDirty(true);
        setStatus('');
    }, []);

    const handleSave = async () => {
        setIsSaving(true);
        setSaveError('');
        try {
            await saveContent(draft);
            setIsDirty(false);
            setStatus('All changes saved.');
        } catch (err) {
            setSaveError(err.message || 'Could not save changes.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDiscard = () => {
        setDraft(cloneValue(content));
        setIsDirty(false);
        setSaveError('');
        setStatus('Changes discarded.');
    };

    const handleReset = async () => {
        setIsSaving(true);
        setSaveError('');
        try {
            await resetContent();
            setIsDirty(false);
            setShowResetConfirm(false);
            setStatus('Content restored to the original wording.');
        } catch (err) {
            setSaveError(err.message || 'Could not reset content.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <AdminShell
            title="Page Content"
            subtitle="Edit any text or image on the website"
            viewLink={page ? page.path : '/'}
        >
            {error && (
                <div className="bg-red-500/20 border-b border-red-500 px-4 py-3">
                    <div className="container mx-auto">
                        <p className="text-red-400 text-sm">{error}</p>
                    </div>
                </div>
            )}

            {storageMode !== 'api' && (
                <div className="bg-yellow-500/10 border-b border-yellow-500/40 px-4 py-3">
                    <div className="container mx-auto">
                        <p className="text-yellow-500 text-sm">
                            Prototype mode - changes and uploaded images are saved in this browser only.
                            On the live site they are saved to the server for everyone.
                        </p>
                    </div>
                </div>
            )}

            <main className="container mx-auto px-4 py-8 pb-28">
                {isLoading ? (
                    <p className="text-gray-400">Loading content...</p>
                ) : (
                    <div className="flex flex-col lg:flex-row gap-8">
                        {/* Page list */}
                        <aside className="lg:w-64 flex-shrink-0 lg:sticky lg:top-32 lg:self-start lg:max-h-[calc(100vh-11rem)] lg:overflow-y-auto">
                            <h2 className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-3">Pages</h2>
                            <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                                {CONTENT_SCHEMA.map(p => (
                                    <button
                                        key={p.key}
                                        onClick={() => setActivePage(p.key)}
                                        className={`text-left px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                                            p.key === activePage
                                                ? 'bg-primary-red text-white font-semibold'
                                                : 'text-gray-300 hover:bg-gray-800'
                                        }`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </nav>
                        </aside>

                        {/* Fields for the selected page */}
                        <div className="flex-grow min-w-0 space-y-6">
                            <div>
                                <h2 className="font-poppins font-light text-2xl text-white">{page.label}</h2>
                                <p className="text-gray-400 text-sm mt-1">
                                    {page.description ? `${page.description} ` : ''}
                                    <a
                                        href={page.path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary-red hover:underline"
                                    >
                                        Open {page.path} →
                                    </a>
                                </p>
                            </div>

                            {page.groups.map(group => (
                                <section key={group.key} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                                    <h3 className="font-poppins font-semibold text-white text-lg mb-1">{group.label}</h3>
                                    {group.description && (
                                        <p className="text-gray-500 text-sm mb-4">{group.description}</p>
                                    )}

                                    <div className="space-y-5 mt-4">
                                        {group.fields.map(field => {
                                            const value = draft[page.key] && draft[page.key][group.key]
                                                ? draft[page.key][group.key][field.key]
                                                : undefined;
                                            const onChange = (v) => setFieldValue(page.key, group.key, field.key, v);

                                            return field.type === 'list' ? (
                                                <ListEditor
                                                    key={field.key}
                                                    field={field}
                                                    value={value}
                                                    onChange={onChange}
                                                    extraImages={extraImages}
                                                />
                                            ) : (
                                                <FieldEditor
                                                    key={field.key}
                                                    field={field}
                                                    value={value}
                                                    onChange={onChange}
                                                    extraImages={extraImages}
                                                />
                                            );
                                        })}
                                    </div>
                                </section>
                            ))}

                            <div className="pt-4">
                                <button
                                    onClick={() => setShowResetConfirm(true)}
                                    className="px-4 py-2 text-gray-400 hover:text-red-400 text-sm transition-colors"
                                >
                                    Reset all pages to the original wording
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>

            {/* Sticky save bar */}
            <div className="fixed bottom-0 inset-x-0 z-40 bg-gray-800 border-t border-gray-700">
                <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
                    <div className="text-sm">
                        {saveError ? (
                            <span className="text-red-400">{saveError}</span>
                        ) : isDirty ? (
                            <span className="text-yellow-400">● Unsaved changes</span>
                        ) : (
                            <span className="text-gray-400">{status || 'Everything is up to date'}</span>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleDiscard}
                            disabled={!isDirty || isSaving}
                            className={`px-6 py-2 transition-colors ${
                                !isDirty || isSaving ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 hover:text-white'
                            }`}
                        >
                            Discard
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!isDirty || isSaving}
                            className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                                !isDirty || isSaving
                                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                    : 'bg-primary-red hover:bg-primary-dark-red text-white'
                            }`}
                        >
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Reset confirmation */}
            {showResetConfirm && (
                <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4">
                    <div className="bg-gray-800 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl p-6">
                        <h3 className="font-poppins font-semibold text-xl text-white mb-4">
                            Reset all content?
                        </h3>
                        <p className="text-gray-400 mb-6">
                            Every text and image change you have made across the whole site will be
                            undone and the original wording restored. This cannot be undone.
                        </p>
                        <div className="flex items-center justify-end gap-3">
                            <button
                                onClick={() => setShowResetConfirm(false)}
                                className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleReset}
                                disabled={isSaving}
                                className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-semibold"
                            >
                                Reset everything
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AdminShell>
    );
};

export default AdminContent;
